import anime from 'animejs/lib/anime.es'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { registerSound } from '@/modules/sound'
import { getRandomIntInRange } from '@/utils'

const sounds = {
  light: registerSound('games/dice/light', { volume: 0.3 }),
  fuse: registerSound('games/dice/fuse', { volume: 0.3 }),
  dud: registerSound('games/cases/dud', { volume: 0.65 }),
  debris: [
    registerSound('games/dice/debris-1', { volume: 0.35 }),
    registerSound('games/dice/debris-2', { volume: 0.35 }),
    registerSound('games/dice/debris-3', { volume: 0.35 }),
    registerSound('games/dice/debris-4', { volume: 0.35 }),
  ],
  explosions: [
    registerSound('games/dice/explosion-1', { volume: 0.6 }),
    registerSound('games/dice/explosion-2', { volume: 0.6 }),
  ],
  impact: [
    registerSound('games/dice/impact-1', { volume: 0.35 }),
    registerSound('games/dice/impact-2', { volume: 0.35 }),
    registerSound('games/dice/impact-3', { volume: 0.35 }),
    registerSound('games/dice/impact-4', { volume: 0.35 }),
  ],
  throw: [
    registerSound('games/dice/throw-1', { volume: 0.9 }),
    registerSound('games/dice/throw-2', { volume: 0.7 }),
    registerSound('games/dice/throw-3', { volume: 0.6 }),
  ],
  win: registerSound('games/wheel/win', { volume: 0.1 }),
}

const TICKETS = 1e4
const MIN_TICKET = 1
const MAX_TICKET = 9999

const FUSE_FAST = 500
const FUSE_SLOW = 2500

export default {
  metaInfo() {
    return {
      title: this.$t('common.games.dice'),
    }
  },
  components: {
    Autobet: () => import('./components/Autobet'),
  },
  data() {
    return {
      loading: {
        roll: false,
      },
      animation: null,
      roll: 0,
      rolling: false,
      sliding: false,
      typing: {
        chance: false,
        multiplier: false,
      },
      inputs: {
        autobet: {
          active: false,
          autobetting: false,
          amountLeft: 0,
          base: 0,
          profit: 0,
          //
          amount: 0,
          onWinAction: 'reset',
          onWinValue: 0,
          onLossAction: 'reset',
          onLossValue: 0,
          stopOnProfit: 0,
          stopOnLoss: 0,
        },
        fast: false,
        over: false,
        amount: 0,
        target: 0,
        multiplier: 0,
        chance: 0,
      },
      history: [],
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/session',
      conga: 'conga/conga',
      settings: 'settings/settings',
    }),
    muted() {
      return !this.settings.games?.dice?.sounds
    },
    config() {
      return this.conga.games.dice
    },
    rules() {
      const { over } = this.inputs
      const { edge } = this.config

      const rules = {}

      const lowerBound = (TICKETS * (Math.ceil(edge * 100) + 1)) / 100
      const upperBound = (TICKETS * (100 - (Math.ceil(edge * 100) + 1))) / 100

      rules.minMult = ((1 - edge) / (MAX_TICKET - lowerBound)) * TICKETS
      rules.maxMult = Math.floor((1 - edge) * TICKETS)

      rules.minChance = MIN_TICKET / 100
      rules.maxChance = upperBound / 100

      // targets
      if (over) {
        rules.minTarget = lowerBound
        rules.maxTarget = MAX_TICKET
      } else {
        rules.minTarget = MIN_TICKET
        rules.maxTarget = upperBound
      }

      return rules
    },
    profit() {
      const { maxProfit, edge } = this.config
      const { amount, target, over } = this.inputs

      let mult
      if (over) {
        mult = ((1 - edge) / (1 - (target / TICKETS)))
      } else {
        mult = ((1 - edge) / (target / TICKETS))
      }

      // backend floors too
      return Math.min(Math.floor(amount * mult), maxProfit)
    },
  },
  watch: {
    'inputs.autobet.active': function onChange(active) {
      if (!active) {
        this.resetAutobet()
      }
    },
  },
  created() {
    this.onMultiplierInput(2, true)
  },
  beforeDestroy() {
    this.resetAutobet()

    this.animation?.pause()
    sounds.fuse.stop()
  },
  methods: {
    ...mapMutations({
      setSettings: 'settings/setSettings',
      addOrRemoveBalance: 'user/addOrRemoveBalance',
    }),
    ...mapActions({
      bet: 'games/dice/bet',
    }),
    async startAutobet() {
      if (this.loading.roll) return
      const { inputs } = this
      const { autobet } = inputs

      autobet.autobetting = true
      autobet.amountLeft = autobet.amount
      autobet.base = inputs.amount

      this.onBet(true)
    },
    onAutoBet(bet, won) {
      const { inputs, config } = this
      const { autobet } = inputs

      if (!autobet.autobetting) {
        this.resetAutobet()
        this.loading.roll = false
        return
      }

      if (autobet.amount > 0) {
        autobet.amountLeft -= 1

        if (autobet.amountLeft < 1) {
          this.resetAutobet()
          this.loading.roll = false
          return
        }
      }

      // do a buncha shit
      const isWon = !!won

      let betAmount = inputs.amount
      let shouldStop = false


      if (isWon) {
        autobet.profit += won - bet
        if (autobet.stopOnProfit && autobet.profit >= autobet.stopOnProfit) {
          shouldStop = true
        }

        if (autobet.onWinAction === 'reset') {
          betAmount = autobet.base
        } else if (autobet.onWinAction === 'increase') {
          if (autobet.onWinValue) {
            const mult = 1 + (autobet.onWinValue / 100)
            betAmount *= mult
          }
        }
      } else {
        autobet.profit -= bet
        if (autobet.stopOnLoss && autobet.profit <= -autobet.stopOnLoss) {
          shouldStop = true
        }

        if (autobet.onLossAction === 'reset') {
          betAmount = autobet.base
        } else if (autobet.onLossAction === 'increase') {
          if (autobet.onLossValue) {
            const mult = 1 + (autobet.onLossValue / 100)
            betAmount *= mult
          }
        }
      }

      if (shouldStop) {
        this.loading.roll = false
        this.resetAutobet()

        return
      }

      betAmount = Math.min(config.maxBet, Math.max(config.minBet, Math.round(betAmount)))
      this.inputs.amount = betAmount

      if (autobet.amount === 0 || autobet.amountLeft > 0) {
        this.onBet(true)
      } else {
        this.loading.roll = false
        this.resetAutobet()
      }
    },
    async onBet(isAutobetting = false) {
      if (this.loading.roll && !isAutobetting) return

      this.loading.roll = true
      const { inputs } = this

      try {
        const bet = await this.bet({
          amount: inputs.amount,
          target: inputs.target,
          over: inputs.over,
        })

        // scroll up for mobile devices
        if (!isAutobetting && this.$vuetify.breakpoint.smAndDown) {
          this.$vuetify.goTo(0, { cancellable: true })
        }

        const fuseTime = inputs.fast ? FUSE_FAST : (FUSE_SLOW + anime.random(-1e3, 600))

        // put balance credit on timer cuz
        if (bet.won) {
          setTimeout(() => {
            this.addOrRemoveBalance(bet.won)
          }, fuseTime + 1e3)
        }


        await this.onRoll(bet.roll, bet.won > 0, fuseTime, inputs.fast)

        this.addHistoryRound({
          id: bet.betId,
          roll: bet.roll,
          won: bet.won,
          multiplier: inputs.multiplier,
        })

        if (bet.won && !isAutobetting && !this._isDestroyed) {
          this.$toast.success(
            this.$tc('common.won_scrap', this.$options.filters.toScrap(bet.won)),
            { title: 'Beancan exploded!', sound: sounds.win },
          )
        }

        if (isAutobetting) {
          this.onAutoBet(inputs.amount, bet.won)
        } else {
          this.loading.roll = false
        }
      } catch (error) {
        this.$toast.error(error.message)

        if (isAutobetting) {
          this.resetAutobet()
        }

        this.loading.roll = false
      }
    },
    async onRoll(roll, won, fuseTime, fast) {
      if (this.rolling) {
        this.animation.stop()
        this.animation.animatables.forEach(({ target }) => {
          anime.remove(target)
        })
      }

      const $stage = this.$refs.stage
      if (!$stage) return

      this.rolling = true

      const $hand = $stage.querySelector('.hand')
      const $door = $stage.querySelector('.door')
      const $beancan = $stage.querySelector('.beancan')
      const $smoke = $stage.querySelector('.sprite-smoke')
      const $roll = $stage.querySelector('.roll')

      const { muted } = this

      this.animation = anime.timeline({
        autoplay: false,
        elasticity: 0,
        easing: 'linear',
        complete() {
          $beancan.classList.remove('on')
        },
        // update: this.onUpdate,
      }).add({
        targets: $door, scale: 1, opacity: 1, duration: 1,
      })
        // throw
        .add({
          targets: $hand,
          // NOTE HACK maybe fuck this off and use a css class tbh
          begin() {
            $beancan.classList.add('on')

            if (muted) return

            sounds.light.play()
          },
          duration: 300,
        })
        // stick beancan with lit fuse
        .add({
          targets: $beancan,
          opacity: [
            { value: 1, duration: 50 },
          ],
          scale: [3, 1],
          translateX: [`${anime.random(50, 100)}%`, 0],
          translateY: ['120%', 0],
          rotate: [anime.random(-360, 360), 0],
          duration: 400,
          easing: 'easeOutExpo',
          begin() {
            if (muted) return

            sounds.throw[getRandomIntInRange(0, 2)].play()
            setTimeout(() => {
              sounds.impact[getRandomIntInRange(0, 3)].play()
            }, 220)
          },
        })
        // anticipation
        .add({
          targets: $door,
          scale: [1, fast ? 1.1 : 1.25],
          duration: fuseTime,
          begin() {
            if (muted) return

            sounds.fuse.play()
          },
          complete() {
            sounds.fuse.stop()
          },
        })

      if (won) {
        this.animation
          .add({
            targets: $door,
            opacity: [1, 0],
            duration: 1,
            complete() {
              if (muted) return

              sounds.explosions[getRandomIntInRange(0, 1)].play()

              if (!fast) {
                const debris = sounds.debris[getRandomIntInRange(0, 3)]
                debris.play()
              }
            },
          })
          // explosion
          .add({
            targets: $smoke,
            opacity: [0, 1],
            duration: 1,
          })
          .add({
            targets: $smoke,
            backgroundPosition: [0, '-2400px'],
            duration: 400,
            easing: 'steps(12)',
          })
          // stage shake
          .add({
            targets: $stage,
            translateX: [
              { value: anime.random(-8, 8) * 3, duration: 50 },
              { value: anime.random(-8, 8) * 2, duration: 50 },
              { value: anime.random(-8, 8) * 1, duration: 50 },
              { value: anime.random(-8, 8) * 0.5, duration: 50 },
              { value: 0, duration: 50 },
            ],
            translateY: [
              { value: anime.random(-8, 8) * 3, duration: 50 },
              { value: anime.random(-8, 8) * 2, duration: 50 },
              { value: anime.random(-8, 8) * 1, duration: 50 },
              { value: anime.random(-8, 8) * 0.5, duration: 50 },
              { value: 0, duration: 70 },
            ],
          }, '-=300')
          .add({
            targets: $roll,
            opacity: [0, 1],
            duration: 600,
            easing: 'easeOutBounce',
          }, '-=300')
          .add({
            targets: $roll.querySelector('span'),
            scale: [0, 1],
            rotate: [anime.random(-45, 45), 0],
            duration: 600,
            easing: 'easeOutBounce',
          }, '-=600')
      } else {
        this.animation
          .add({
            targets: $door,
            scale: 1,
            duration: 1,
            complete() {
              $beancan.classList.remove('on')

              if (muted) return
              sounds.dud.play()
            },
          })
          .add({
            targets: $beancan,
            opacity: 0,
            translateY: 200,
            rotate: anime.random(180, 360),
            duration: 300,
          }, '+=500')
          .add({
            targets: $roll,
            opacity: [0, 1],
            translateY: [-75, 0],
            duration: 600,
            easing: 'easeOutCirc',
          }, '-=300')
          .add({
            targets: $roll.querySelector('span'),
            scale: [0, 0.8],
            rotate: [-90, 0],
            duration: 600,
            easing: 'easeOutCirc',
          }, '-=600')
      }


      this.animation.play()
      this.roll = roll / 100

      await this.animation.finished

      this.rolling = false
    },
    changeRoll() {
      if (this.loading.roll) return

      this.inputs.over = !this.inputs.over

      this.inputs.target = 1e4 - this.inputs.target
      this.onTargetChanged(this.inputs.target)
    },
    onSliderInput(val, force = false) {
      if (!this.sliding && !force) return

      // clear focus from form inputs
      if (this.typing.multiplier) {
        this.$refs.multInput.blur()
      }

      if (this.typing.chance) {
        this.$refs.chanceInput.blur()
      }

      if (val === this.inputs.target) return

      if (val === MIN_TICKET || val === MAX_TICKET) {
        this.inputs.target = val
        this.onTargetChanged(this.inputs.target)
        return
      }

      // round value if user is using slider
      this.inputs.target = Math.round(val / 100) * 100
      this.onTargetChanged(this.inputs.target)
    },
    onMultiplierInput(val, force = false) {
      if (this.typing.chance) return
      if (!this.typing.multiplier && !force) return
      if (val === this.inputs.multiplier) return

      this.inputs.multiplier = val

      const { over } = this.inputs
      const { edge } = this.config
      const { rules } = this

      let target = 0
      let chance = 0
      if (over) {
        target = Math.round(TICKETS - (((1 - edge) / val) * TICKETS))
        target = Math.min(rules.maxTarget, Math.max(rules.minTarget, target))

        chance = 100 - (target / 100)
      } else {
        target = Math.round(((1 - edge) / val) * TICKETS)
        target = Math.min(rules.maxTarget, Math.max(rules.minTarget, target))

        chance = target / 100
      }

      this.inputs.target = target
      this.inputs.chance = Math.min(rules.maxChance, Math.max(rules.minChance, chance))
    },
    onChanceInput(val, force = false) {
      if (this.typing.multiplier) return
      if (!this.typing.chance && !force) return
      if (val === this.inputs.chance) return

      this.inputs.chance = val

      const { over } = this.inputs
      const { edge } = this.config
      const { rules } = this

      let target = 0
      let mult = 0
      if (over) {
        target = Math.round((100 - val) * 100)
        target = Math.min(rules.maxTarget, Math.max(rules.minTarget, target))

        mult = ((1 - edge) / (TICKETS - target)) * TICKETS
      } else {
        target = Math.round(val * 100)
        target = Math.min(rules.maxTarget, Math.max(rules.minTarget, target))

        mult = Math.abs(((edge - 1) / target) * TICKETS)
      }

      this.inputs.target = target
      this.inputs.multiplier = Math.min(rules.maxMult, Math.max(rules.minMult, mult))
    },
    onTargetChanged(val) {
      const { edge } = this.config
      const { over } = this.inputs
      const { rules } = this

      let mult = 0
      let chance = 0
      if (over) {
        mult = ((1 - edge) / (TICKETS - val)) * TICKETS
        chance = 100 - (val / 100)
      } else {
        mult = Math.abs(((edge - 1) / val) * TICKETS)
        chance = val / 100
      }

      this.inputs.multiplier = Math.min(rules.maxMult, Math.max(rules.minMult, mult))
      this.inputs.chance = Math.min(rules.maxChance, Math.max(rules.minChance, chance))
    },
    resetAutobet() {
      const { autobet } = this.inputs

      if (autobet.base) {
        this.inputs.amount = autobet.base
      }

      autobet.profit = 0
      autobet.autobetting = false
      autobet.amountLeft = 0
      autobet.base = 0
    },
    onMultiplyBet(mult) {
      if (this.loading.roll) return

      const { maxBet, minBet } = this.config
      const { amount } = this.inputs
      this.inputs.amount = Math.min(maxBet, Math.max(minBet, Math.round(amount * mult)))
    },
    onToggleSounds(val) {
      this.settings.games.dice.sounds = val

      this.setSettings(this.settings)
    },
    addHistoryRound(round) {
      if (this.history.length > 20) {
        this.history.pop()
      }

      this.history.unshift(round)
    },
  },
}
